import { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { FullPostType, RetryState } from 'src/app/pages/starterDashboardV2/shared/components';

import type { Platform } from '../useFetchReadyToSharePostsByPlatform';

export type { RetryState };

export type PostTypeRetryState = Record<FullPostType, RetryState>;

const INITIAL_RETRY_STATE: PostTypeRetryState = {
	facebook_post: 'idle',
	facebook_group_post: 'idle',
	facebook_story: 'idle',
	instagram_post: 'idle',
	instagram_story: 'idle',
	reddit_post: 'idle',
	reddit_group_post: 'idle',
};

/**
 * Maps a FullPostType to its corresponding Platform for query invalidation.
 * Used to determine which platform's cache to invalidate when retrying a specific post type.
 */
function mapPostTypeToPlatform(fullPostType: FullPostType): Platform {
	switch (fullPostType) {
		case 'facebook_post':
		case 'facebook_story':
			return 'FACEBOOK';
		case 'facebook_group_post':
			return 'FACEBOOKGROUPS';
		case 'instagram_post':
		case 'instagram_story':
			return 'INSTAGRAM';
		case 'reddit_post':
		case 'reddit_group_post':
			return 'REDDIT';
		default: {
			// Exhaustive check
			const exhaustiveCheck: never = fullPostType;
			throw new Error(`Unknown post type: ${String(exhaustiveCheck)}`);
		}
	}
}

/**
 * Placeholder for the backend mutation to trigger asset regeneration.
 * This will be replaced with an actual API call once the backend endpoint is implemented.
 *
 * @param _slug - The petition slug
 * @param _fullPostType - The post type to regenerate assets for
 * @returns Promise that resolves when the regeneration is triggered
 */
async function triggerAssetRegeneration(_slug: string, _fullPostType: FullPostType): Promise<void> {
	// TODO: Implement actual backend call once endpoint is available
	// This is a placeholder that simulates a successful trigger
	// The backend should:
	// 1. Regenerate preview links for the platform
	// 2. Regenerate AI messages for the post type
	// 3. Regenerate share visuals/images if needed
	return Promise.resolve();
}

type UseRetryPostGenerationProps = {
	slug: string;
};

type UseRetryPostGenerationResult = ModelHookResult<
	{
		retryStates: PostTypeRetryState;
		isRetrying: boolean;
	},
	{
		retryPostType: (fullPostType: FullPostType) => Promise<void>;
		resetRetryState: (fullPostType: FullPostType) => void;
	}
>;

/**
 * Hook to manage retry functionality for post generation.
 * Handles triggering asset regeneration and re-polling for specific post types.
 */
export function useRetryPostGeneration({ slug }: UseRetryPostGenerationProps): UseRetryPostGenerationResult {
	const queryClient = useQueryClient();
	const [retryStates, setRetryStates] = useState<PostTypeRetryState>(INITIAL_RETRY_STATE);

	const isRetrying = Object.values(retryStates).some((state) => state === 'retrying');

	const retryPostType = useCallback(
		async (fullPostType: FullPostType) => {
			// Set retrying state
			setRetryStates((prev) => ({
				...prev,
				[fullPostType]: 'retrying' as const,
			}));

			try {
				// Trigger backend asset regeneration (placeholder)
				await triggerAssetRegeneration(slug, fullPostType);

				// Get the platform for this post type to invalidate the correct query
				const platform = mapPostTypeToPlatform(fullPostType);

				// Invalidate the React Query cache for this platform
				// This will trigger a refetch and restart polling
				await queryClient.invalidateQueries({
					queryKey: ['readyToSharePosts', slug, platform],
				});

				// Reset to idle after cache invalidation (polling will handle the actual status)
				setRetryStates((prev) => ({
					...prev,
					[fullPostType]: 'idle' as const,
				}));
			} catch {
				// Set failed state on error
				setRetryStates((prev) => ({
					...prev,
					[fullPostType]: 'failed' as const,
				}));
			}
		},
		[slug, queryClient],
	);

	const resetRetryState = useCallback((fullPostType: FullPostType) => {
		setRetryStates((prev) => ({
			...prev,
			[fullPostType]: 'idle' as const,
		}));
	}, []);

	return {
		data: {
			retryStates,
			isRetrying,
		},
		actions: {
			retryPostType,
			resetRetryState,
		},
	};
}

export type { FullPostType };
